/* Blissal TR — cart drawer: open/close, quantity changes, bundle upgrade, and add-to-cart rendering. */

class BlCart extends HTMLElement {
  connectedCallback() {
    this.section = this.querySelector('[id^="shopify-section-"]');
    this.sectionId = this.section?.id.replace('shopify-section-', '') || 'bl-cart-drawer';
    this.addEventListener('click', (event) => this.onClick(event));
    this.querySelector('.bl-cart__overlay')?.addEventListener('click', () => this.close());
    document.addEventListener('keyup', (event) => event.key === 'Escape' && this.close());
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('#cart-icon-bubble');
      // On the cart page itself the full cart is the source of truth; let the link navigate.
      if (!trigger || window.location.pathname.replace(/\/$/, '').endsWith('/cart')) return;
      event.preventDefault();
      this.open(trigger);
    });
    window.blCart = this;
  }

  get sectionIds() {
    return [this.sectionId, 'cart-icon-bubble'];
  }

  open(trigger) {
    this.trigger = trigger || document.activeElement;
    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
    this.querySelector('[data-cart-close]')?.focus();
  }

  close() {
    if (!this.hasAttribute('open')) return;
    this.removeAttribute('open');
    document.body.style.overflow = '';
    this.trigger?.focus?.();
  }

  async onClick(event) {
    if (event.target.closest('[data-cart-close]')) return this.close();
    if (this.busy) return;
    const qtyButton = event.target.closest('[data-key]');
    if (qtyButton) return this.change(qtyButton.dataset.key, Math.max(0, Number(qtyButton.dataset.qty)));
    const addButton = event.target.closest('[data-add]');
    if (addButton) return this.upgrade(addButton.dataset.add, addButton.dataset.replaceProduct);
  }

  async request(url, body) {
    this.busy = true;
    this.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}${url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: this.sectionIds, sections_url: window.location.pathname }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Sepet güncellenemedi, lütfen tekrar deneyin.');
      return data;
    } finally {
      this.busy = false;
      this.removeAttribute('aria-busy');
    }
  }

  async change(key, quantity) {
    try {
      // Line item keys stay valid after re-renders; positional line indexes do not.
      this.render(await this.request('cart/change.js', { id: key, quantity }));
    } catch (err) {
      this.showError(err);
    }
  }

  /* Swaps the bundle of the same product already in the cart for the chosen (bigger) one.
     The new bundle is added first so a failed add never empties the cart. */
  async upgrade(variantId, productId) {
    if (this.busy) return;
    try {
      const cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
      const previous = cart.items.filter((item) => String(item.product_id) === String(productId) && String(item.variant_id) !== String(variantId));
      let data = await this.request('cart/add.js', { items: [{ id: Number(variantId), quantity: 1 }] });
      if (previous.length) {
        const updates = Object.fromEntries(previous.map((item) => [item.key, 0]));
        data = await this.request('cart/update.js', { updates });
      }
      this.render(data);
    } catch (err) {
      this.showError(err);
    }
  }

  render(data) {
    const sections = data.sections || {};
    if (sections[this.sectionId]) {
      const fresh = new DOMParser().parseFromString(sections[this.sectionId], 'text/html').querySelector('.bl-cart__inner');
      if (fresh) this.querySelector('.bl-cart__inner')?.replaceWith(fresh);
    }
    if (sections['cart-icon-bubble']) {
      const bubble = document.getElementById('cart-icon-bubble');
      const html = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
      if (bubble && html) bubble.innerHTML = html.innerHTML;
    }
  }

  showError(err) {
    const inner = this.querySelector('.bl-cart__body') || this.querySelector('.bl-cart__inner');
    let box = this.querySelector('.bl-cart__error');
    if (!box) {
      box = document.createElement('p');
      box.className = 'bl-cart__error';
      box.setAttribute('role', 'alert');
      inner?.prepend(box);
    }
    box.textContent = err.message;
  }
}
customElements.define('bl-cart', BlCart);
